import { ProfilePhoto } from "common/profilePhoto";
import LogoutModal from "components/authentication/logoutModal";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { closeSideBarDrawer, toggleSideBarMode } from "redux/Slice/sideBar";
import { AppDispatch, RootState } from "redux/store";
import { getImageUrl } from "utils";
import { Icons } from "utils/iconPath";
import packageJson from "../../../../package.json";
import "./style.css";
import { Tab } from "./tab";
import { TabDataType, TABS_DATA } from "./tabsData";

export const SideBar = ({ reference }: { reference?: any }) => {
  // Hooks
  const { t } = useTranslation("sideBar");
  const tabGroupRef = useRef<HTMLUListElement>(null);
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  // Global States
  const { supplierProfileDetails, sideBar } = useSelector(
    (state: RootState) => ({
      supplierProfileDetails: state?.supplierProfile?.supplierProfileRes,
      sideBar: state?.sideBar,
    }),
    shallowEqual
  );

  // Variables
  let pageName = location?.pathname?.split("/");

  // Local State
  const [logoutModalVisibility, setLogoutModalVisibility] =
    useState<boolean>(false);

  // Functions
  const handleSideBarScroll = (tabId: string) => {
    const tab = document?.getElementById(tabId);
    if (tab && tabGroupRef?.current) {
      tabGroupRef?.current.scrollTo({
        top: tab?.offsetTop - tabGroupRef?.current?.offsetTop,
        behavior: "smooth",
      });
    }
  };

  return (
    <>
      <div
        className="SideBar"
        ref={reference}
        style={{
          width:
            sideBar?.mode === "expanded"
              ? "var(--side-bar-expanded-width)"
              : "var(--side-bar-collapsed-width)",
        }}
      >
        <div
          className="logoAndVersion"
          style={{
            flexDirection: sideBar?.mode === "expanded" ? "row" : "column",
          }}
        >
          <Link to={"/"} className="logoContainer clickable">
            <img className="logo" src={Icons.LOGO} alt="logo" />
          </Link>

          {packageJson?.version ? (
            <p
              className="version description"
              style={{
                marginRight: sideBar?.mode === "expanded" ? "1.5rem" : "",
              }}
            >
              {`${sideBar?.mode === "expanded" ? "Version" : "V"} ${
                packageJson.version
              }`}
            </p>
          ) : null}
        </div>

        <ul className="sectionsContainer" ref={tabGroupRef}>
          {TABS_DATA?.map((item: TabDataType, index: number) => {
            const link = item?.link?.split("/");
            return (
              <li key={index} onClick={() => dispatch(closeSideBarDrawer())}>
                <Tab
                  icon={item?.icon}
                  label={t(item?.label)}
                  isActive={pageName[1] === link[1]}
                  id={item?.label}
                  onClick={() => {
                    if (item.key === "live-dashboard") {
                      window.open(item.link, "_blank");
                      return;
                    }
                    handleSideBarScroll(item?.label);
                    navigate(item.link);
                  }}
                />
              </li>
            );
          })}
        </ul>

        <div className="separator bottomSeparator"></div>

        <div
          className="user unselectable "
          style={{
            justifyContent:
              sideBar?.mode === "expanded" ? "space-between" : "center",
            padding: sideBar?.mode === "expanded" ? "" : "0",
          }}
        >
          {sideBar?.mode === "expanded" ? (
            <div className="leftSection">
              <ProfilePhoto
                name={supplierProfileDetails?.company_name}
                photo={getImageUrl(supplierProfileDetails?.logo)}
                size={"4.5rem"}
                containerStyle={{ backgroundColor: "rgb(var(--black),0.1)" }}
                nameStyle={{ color: "rgb(var(--black))" }}
              />
              <div className="nameAndRole">
                {supplierProfileDetails?.company_name && (
                  <h6 className="name">
                    {supplierProfileDetails?.company_name}
                  </h6>
                )}
                {supplierProfileDetails?.contact_number && (
                  <p className="role">
                    {supplierProfileDetails?.contact_number}
                  </p>
                )}
              </div>
            </div>
          ) : null}
          <div
            className="rightSection clickable"
            onClick={() => setLogoutModalVisibility(true)}
            style={{
              width: sideBar?.mode === "expanded" ? "" : "100%",
            }}
          >
            <img src={Icons?.LOGOUT} className="icon" alt="icon" />
          </div>

          <img
            className="toggleSideBarModeIcon"
            src={Icons?.CHANGE_SIDE_BAR_MODE}
            onClick={() => {
              dispatch(toggleSideBarMode());
            }}
            style={{
              transform:
                sideBar?.mode === "expanded"
                  ? "rotate(180deg)"
                  : "rotate(0deg)",
            }}
            title="sidebar icon"
            alt="icon"
          />
        </div>
      </div>

      {/* Logout Modal */}
      {logoutModalVisibility ? (
        <LogoutModal
          logout_modalVisibility={logoutModalVisibility}
          onClose={() => setLogoutModalVisibility(false)}
        />
      ) : null}
    </>
  );
};
